import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

interface UserCurrency {
  id: string;
  user_id: string;
  coins: number;
  gems: number;
  lifetime_coins_earned: number;
  lifetime_gems_earned: number;
  created_at: string;
  updated_at: string;
}

interface CurrencyTransaction {
  id: string;
  user_id: string;
  transaction_type: 'earned' | 'spent';
  currency_type: 'coins' | 'gems';
  amount: number;
  reason: string;
  source_data: any;
  created_at: string;
}

export function useCurrency() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [currency, setCurrency] = useState<UserCurrency | null>(null);
  const [transactions, setTransactions] = useState<CurrencyTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const hasInitialized = useRef(false);

  // Reset initialization flag when user changes
  useEffect(() => {
    hasInitialized.current = false;
  }, [user?.id]);

  // Fetch user currency
  const fetchCurrency = async () => {
    console.log('💰 fetchCurrency called, user ID:', user?.id);
    
    if (!user?.id) {
      console.log('❌ No user ID, setting loading to false');
      setLoading(false);
      return;
    }

    try {
      console.log('🔍 Querying user_currencies table...');
      const { data, error } = await supabase
        .from('user_currencies')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('❌ Error fetching currency:', error);
        return;
      }

      console.log('💳 Currency data received:', data);
      setCurrency(data || null);
    } catch (error) {
      console.error('❌ Error in fetchCurrency:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch recent transactions
  const fetchTransactions = async () => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from('currency_transactions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) {
        console.error('Error fetching transactions:', error);
        return;
      }

      setTransactions(data || []);
    } catch (error) {
      console.error('Error in fetchTransactions:', error);
    }
  };

  // Initialize currency for new users ONLY - uses ignoreDuplicates to prevent overwriting
  const initializeCurrency = async () => {
    if (!user?.id) return;

    try {
      console.log('💰 Initializing currency for new user (ignoreDuplicates: true)');
      const { error } = await supabase
        .from('user_currencies')
        .upsert([
          {
            user_id: user.id,
            coins: 100,
            gems: 5,
            lifetime_coins_earned: 100,
            lifetime_gems_earned: 5,
          },
        ], {
          onConflict: 'user_id',
          ignoreDuplicates: true  // CRITICAL: Don't overwrite existing data!
        });

      if (error && !error.message.includes('duplicate key')) {
        console.error('Error initializing currency:', error);
      } else {
        fetchCurrency();
      }
    } catch (error) {
      console.error('Error in initializeCurrency:', error);
    }
  };

  // Award currency (used by game completion)
  const awardCurrency = async (
    coins: number,
    gems: number,
    reason: string,
    sourceData?: any,
    retryCount: number = 0,
    sessionRefreshAttempted: boolean = false
  ) => {
    console.log('🔥 awardCurrency called with:', { coins, gems, reason, sourceData, userId: user?.id, retryCount });
    
    if (!user?.id) {
      console.error('❌ No user ID for currency award. Auth state:', { 
        user, 
        hasSession: !!user, 
        userId: user?.id 
      });
      
      // Try to refresh the session once
      if (!sessionRefreshAttempted) {
        console.log('🔄 Attempting to refresh session...');
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user?.id) {
          console.log('✅ Session refreshed, retrying award...');
          return awardCurrency(coins, gems, reason, sourceData, retryCount, true);
        }
      }
      
      toast({
        title: "Authentication Required",
        description: "Please log in again to receive your rewards.",
        variant: "destructive"
      });
      throw new Error('USER_NOT_AUTHENTICATED');
    }

    try {
      const startTime = performance.now();
      
      console.log('💳 [PRE-RPC] Calling supabase.rpc award_currency with:', {
        _user_id: user.id,
        _coins: coins,
        _gems: gems,
        _reason: reason,
        _source_data: sourceData,
        timestamp: new Date().toISOString()
      });
      
      const { data, error } = await supabase.rpc('award_currency', {
        _user_id: user.id,
        _coins: coins,
        _gems: gems,
        _reason: reason,
        _source_data: sourceData || {},
      });

      const elapsed = performance.now() - startTime;

      if (error) {
        console.error('❌ [RPC-ERROR] Error awarding currency:', {
          error,
          errorMessage: error.message,
          errorCode: error.code,
          errorDetails: error.details,
          errorHint: error.hint,
          fullError: JSON.stringify(error),
          elapsed: `${elapsed.toFixed(2)}ms`,
          retryCount
        });
        
        // Retry once if rate limit error (with 1000ms delay to match new rate limit)
        if (error.message?.includes('rate_limit_exceeded') && retryCount === 0) {
          console.log('⏳ Rate limit hit, retrying in 1000ms...');
          await new Promise(resolve => setTimeout(resolve, 1000));
          return awardCurrency(coins, gems, reason, sourceData, 1);
        }
        
        // Show detailed error toast with hint if available
        toast({
          title: "Currency Award Failed",
          description: error.hint || error.message || "Unable to award currency. Please try again.",
          variant: "destructive",
          duration: 5000
        });
        
        return null;
      }

      console.log('✅ [RPC-SUCCESS] RPC response:', {
        data,
        elapsed: `${elapsed.toFixed(2)}ms`,
        timestamp: new Date().toISOString()
      });
      
      // Parse the JSON response properly
      const result = data as { coins: number; gems: number; coins_earned: number; gems_earned: number };

      console.log('💰 Updating local currency state from:', currency, 'to result:', result);
      
      // Update local currency state
      setCurrency(prev => prev ? {
        ...prev,
        coins: result.coins,
        gems: result.gems,
        lifetime_coins_earned: prev.lifetime_coins_earned + (coins > 0 ? coins : 0),
        lifetime_gems_earned: prev.lifetime_gems_earned + (gems > 0 ? gems : 0),
        updated_at: new Date().toISOString(),
      } : null);

      // Show currency change notification
      const actualCoinsChange = result.coins_earned;
      if (actualCoinsChange !== 0 || gems > 0) {
        const isLoss = actualCoinsChange < 0;
        console.log('🎉 Showing currency notification:', { actualCoinsChange, gems, isLoss });
        toast({
          title: isLoss ? "Currency Lost 😔" : "Currency Earned! 🎉",
          description: isLoss 
            ? `${actualCoinsChange} coins` 
            : `+${actualCoinsChange} coins${gems > 0 ? `, +${gems} gems` : ''}`,
          duration: 3000,
          variant: isLoss ? "destructive" : "default"
        });
      }

      // Refresh transactions and trigger UI update
      fetchTransactions();
      window.dispatchEvent(new CustomEvent('currency-updated'));

      return result;
    } catch (error) {
      console.error('❌ Error in awardCurrency:', error);
      
      // Retry once on unexpected errors
      if (retryCount === 0) {
        console.log('⏳ Unexpected error, retrying in 500ms...');
        await new Promise(resolve => setTimeout(resolve, 500));
        return awardCurrency(coins, gems, reason, sourceData, 1);
      }
      
      return null;
    }
  };

  // Set up real-time subscription for currency updates
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel('user-currency-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_currencies',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          console.log('Currency real-time update:', payload);
          if (payload.eventType === 'UPDATE') {
            setCurrency(payload.new as UserCurrency);
          }
        }
      )
      .subscribe();

    // Listen for custom currency update events
    const handleCurrencyUpdate = () => {
      console.log('Manual currency refresh triggered');
      fetchCurrency();
    };
    
    window.addEventListener('currency-updated', handleCurrencyUpdate);

    return () => {
      supabase.removeChannel(channel);
      window.removeEventListener('currency-updated', handleCurrencyUpdate);
    };
  }, [user?.id, fetchCurrency]);

  // Initial data fetch
  useEffect(() => {
    if (user?.id) {
      fetchCurrency();
      fetchTransactions();
    } else {
      setCurrency(null);
      setTransactions([]);
      setLoading(false);
    }
  }, [user?.id]);

  // Initialize currency if user exists but no currency record (only once per session)
  useEffect(() => {
    if (user?.id && !loading && !currency && !hasInitialized.current) {
      hasInitialized.current = true;
      initializeCurrency();
    }
  }, [user?.id, loading, currency]);

  return {
    currency,
    transactions,
    loading,
    awardCurrency,
    refreshCurrency: fetchCurrency,
    refreshTransactions: fetchTransactions,
  };
}